import { Copy } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { agentSetupCommands, type AgentSetup, type AgentSetupMode } from '../../lib/agent-setup';
import { Button } from '../../ui/Button';
import { Card } from '../../ui/misc';

/** Claude Code plugin setup: copyable commands, and the working rules the plugin follows. */
export function AgentSetupGuide({
  setup,
  mode,
  children,
}: {
  setup: AgentSetup;
  mode: AgentSetupMode;
  children?: ReactNode;
}) {
  const [copied, setCopied] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const commands = agentSetupCommands(setup, mode);
  const copy = () =>
    navigator.clipboard.writeText(commands).then(
      () => setCopied(true),
      () => undefined,
    );
  return (
    <Card
      title="Claude Code setup"
      subtitle={
        mode.kind === 'personal'
          ? 'Paste these into Claude Code. The login opens this site; sign in and choose Allow. Then ask /lixionary-jira:jira for anything, such as "implement APP-42".'
          : 'Run the plugin CLI with these variables on the builder. It exchanges the client ID and secret for one-hour access tokens itself.'
      }
      action={
        <Button size="sm" icon={<Copy size={14} />} onClick={copy}>
          {copied ? 'Copied' : 'Copy'}
        </Button>
      }
    >
      <div className="stack sm">
        <pre
          className="mono small"
          role="region"
          aria-label="Setup commands"
          tabIndex={0}
          style={{ whiteSpace: 'pre-wrap', margin: 0 }}
        >
          {commands}
        </pre>
        {children}
        <button
          type="button"
          className="btn btn-ghost sm"
          style={{ alignSelf: 'flex-start' }}
          aria-expanded={showRules}
          onClick={() => setShowRules((v) => !v)}
        >
          {showRules ? 'Hide working rules' : 'Show working rules'}
        </button>
        {showRules && (
          <pre
            className="mono small"
            role="region"
            aria-label="Working rules"
            tabIndex={0}
            style={{ whiteSpace: 'pre-wrap', margin: 0, maxHeight: 360, overflow: 'auto' }}
          >
            {setup.instructions}
          </pre>
        )}
      </div>
    </Card>
  );
}
